export const theme = {
  colors: {
    primary: '#3a2462', // 紫微主星深空紫（导航/主按钮）
    accent: '#c9a145', // 星曜金色（重点标记/交互反馈）
    background: '#f8f5f0', // 月白主背景（暖灰调防眩光）
    surface: '#FFFFFF',
    text: '#1a1a1a', // 墨黑正文/标题（确保WCAG AA对比度）
    placeholder: '#708090', // Slate Gray
    backdrop: 'rgba(0, 0, 0, 0.5)',
    notification: '#FF6347', // Tomato
    error: '#DC143C', // Crimson
    success: '#2a7a7b', // 道家青绿（辅助色）
    warning: '#FF8C00', // Dark Orange
    // Chinese astrology specific colors
    wood: '#2a7a7b', // 道家青绿
    fire: '#DC143C', // Crimson
    earth: '#8B4513', // Saddle Brown
    metal: '#c9a145', // 星曜金色
    water: '#3a2462', // 紫微主星深空紫
    // Star colors
    mainStar: '#3a2462', // 紫微主星深空紫
    auxStar: '#4b2e8a', // 渐变紫色
    luckyStars: '#c9a145', // 星曜金色
    unluckyStars: '#696969', // Dim Gray
    // 新增暗黑模式颜色
    darkBackground: '#121212',
    darkText: '#e0e0e0',
    darkPrimary: '#5d3fb1', // 适当提亮紫色
  },
  fonts: {
    regular: {
      fontFamily: '"Inter", "Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 'normal',
    },
    medium: {
      fontFamily: '"Inter", "Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: '500',
    },
    light: {
      fontFamily: '"Inter", "Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: '300',
    },
    thin: {
      fontFamily: '"Inter", "Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontWeight: '100',
    },
  },
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

// 响应式间距系统
export const responsiveSpacing = {
  // 基于屏幕尺寸的动态间距
  container: {
    xs: '1rem',
    sm: '1.5rem',
    md: '2rem',
    lg: '3rem',
    xl: '4rem',
  },
  section: {
    xs: '2rem',
    sm: '3rem',
    md: '4rem',
    lg: '5rem',
    xl: '6rem',
  },
  component: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
  },
  xs: {
    xs: '0.25rem',
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.625rem',
    xl: '0.75rem',
  },
};

// 响应式断点
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// 响应式字体大小
export const responsiveFontSizes = {
  h1: {
    xs: '1.75rem',
    sm: '2rem',
    md: '2.5rem',
    lg: '3rem',
    xl: '3.5rem',
  },
  h2: {
    xs: '1.5rem',
    sm: '1.75rem',
    md: '2rem',
    lg: '2.25rem',
    xl: '2.5rem',
  },
  h3: {
    xs: '1.25rem',
    sm: '1.375rem',
    md: '1.5rem',
    lg: '1.75rem',
    xl: '2rem',
  },
  h4: {
    xs: '1.125rem',
    sm: '1.25rem',
    md: '1.375rem',
    lg: '1.5rem',
    xl: '1.625rem',
  },
  h5: {
    xs: '1rem',
    sm: '1.125rem',
    md: '1.25rem',
    lg: '1.375rem',
    xl: '1.5rem',
  },
  h6: {
    xs: '0.875rem',
    sm: '1rem',
    md: '1.125rem',
    lg: '1.25rem',
    xl: '1.375rem',
  },
  body1: {
    xs: '0.875rem',
    sm: '0.9375rem',
    md: '1rem',
    lg: '1.0625rem',
    xl: '1.125rem',
  },
  body2: {
    xs: '0.75rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '0.9375rem',
    xl: '1rem',
  },
  button: {
    xs: '0.8125rem',
    sm: '0.875rem',
    md: '0.9375rem',
    lg: '1rem',
    xl: '1.0625rem',
  },
};

export const borderRadius = {
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
};

// 响应式边框半径
export const responsiveBorderRadius = {
  xs: '0.25rem',
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
};

// 响应式阴影
export const responsiveShadows = {
  card: {
    xs: '0 2px 8px rgba(0,0,0,0.1)',
    sm: '0 4px 12px rgba(0,0,0,0.1)',
    md: '0 6px 16px rgba(0,0,0,0.12)',
    lg: '0 8px 24px rgba(0,0,0,0.15)',
  },
  button: {
    xs: '0 1px 3px rgba(0,0,0,0.12)',
    sm: '0 2px 6px rgba(0,0,0,0.15)',
    md: '0 4px 12px rgba(0,0,0,0.18)',
  },
};

// 媒体查询辅助函数
export const mediaQueries = {
  up: (breakpoint: keyof typeof breakpoints) => `@media (min-width: ${breakpoints[breakpoint]}px)`,
  down: (breakpoint: keyof typeof breakpoints) => {
    const value = breakpoints[breakpoint];
    return `@media (max-width: ${value - 0.02}px)`;
  },
  between: (start: keyof typeof breakpoints, end: keyof typeof breakpoints) => 
    `@media (min-width: ${breakpoints[start]}px) and (max-width: ${breakpoints[end] - 0.02}px)`,
  only: (breakpoint: keyof typeof breakpoints) => {
    const keys = Object.keys(breakpoints) as Array<keyof typeof breakpoints>;
    const index = keys.indexOf(breakpoint);
    const nextBreakpoint = keys[index + 1];
    
    if (nextBreakpoint) {
      return mediaQueries.between(breakpoint, nextBreakpoint);
    } else {
      return mediaQueries.up(breakpoint);
    }
  },
};